import {
  type Branding,
  type BrandingColoresResuelto,
  derivarNeutrales,
  normalizarColores,
} from "@copi/shared";

/**
 * Mapea una paleta resuelta a las CSS variables de la **capa 1** (stops de marca
 * + acentos semánticos + neutros tintados). Único lugar que conoce ese mapeo: lo
 * usan tanto `applyBranding` (runtime global) como el preview del panel de
 * personalización.
 *
 * Si la paleta trae `fondo`, se derivan las superficies neutras de AMBOS temas y
 * se emiten como `--neutral-l-*` (claro) / `--neutral-d-*` (oscuro), que son
 * theme-independientes: `globals.css` elige el set correcto por selector
 * (`:root` vs `.dark`) con fallback a los neutros navy por defecto. Sin `fondo`,
 * no se emite ninguna ⇒ el fallback navy queda intacto (cero regresión).
 */
export function coloresACssVars(c: BrandingColoresResuelto): Record<string, string> {
  const vars: Record<string, string> = {
    "--brand-stop-0": c.brand50,
    "--brand-stop-1": c.brand100,
    "--brand-stop-2": c.brand200,
    "--brand-stop-3": c.brand300,
    "--brand-stop-4": c.brand400,
    "--brand-stop-5": c.brand500,
    "--brand-stop-6": c.brand600,
    "--brand-stop-7": c.brand700,
    "--brand-stop-8": c.brand800,
    "--brand-stop-9": c.brand900,
    "--gold-500": c.gold500,
    "--success-50": c.success50,
    "--success-400": c.success400,
    "--success-500": c.success500,
    "--success-600": c.success600,
    "--success-700": c.success700,
    "--info-50": c.info50,
    "--info-400": c.info400,
    "--info-500": c.info500,
    "--info-600": c.info600,
    "--info-700": c.info700,
  };

  if (c.fondo) {
    const n = derivarNeutrales(c.fondo);
    if (n) {
      vars["--neutral-l-bg"] = n.bgClaro;
      vars["--neutral-l-card"] = n.cardClaro;
      vars["--neutral-l-muted"] = n.mutedClaro;
      vars["--neutral-l-border"] = n.borderClaro;
      vars["--neutral-d-bg"] = n.bgOscuro;
      vars["--neutral-d-card"] = n.cardOscuro;
      vars["--neutral-d-muted"] = n.mutedOscuro;
      vars["--neutral-d-border"] = n.borderOscuro;
    }
  }

  return vars;
}

/**
 * Aplica un `Branding` al documento (ADR 039 / 047 / 048).
 *
 * Inyecta SÓLO la **capa 1** de tokens (paleta RAW, independiente del tema):
 * los `--brand-stop-0..9` (rampa de marca), `--gold-500` y las rampas semánticas
 * `--success-*` / `--info-*`. La **capa 2** (`--brand-50..900`) y la **capa 3**
 * (roles `--primary`, `--background`, estados…) viven en `globals.css` y derivan
 * de la capa 1 — por eso NO se setean acá: así el modo claro/oscuro (que conmuta
 * la capa 2/3 vía la clase `.dark`) funciona para cualquier institución sin que
 * un estilo inline las pise.
 *
 * Además:
 *  - setea `data-sidebar` (estilo de barra del tenant: claro/oscuro);
 *  - apunta el favicon al logo de la institución (si tiene);
 *  - actualiza `document.title`.
 *
 * Idempotente respecto al `style` inline: llamarla N veces deja el mismo estado.
 * Pura respecto a su entrada (no lee estado global); sólo escribe en el DOM.
 */
export function applyBranding(branding: Branding): void {
  const root = document.documentElement;
  // Normalizamos para tener SIEMPRE la rampa completa, incluso si el wire trae
  // una paleta parcial (filas creadas antes de los tokens extendidos).
  const vars = coloresACssVars(normalizarColores(branding.colores));

  for (const [nombre, valor] of Object.entries(vars)) {
    root.style.setProperty(nombre, valor);
  }

  root.setAttribute("data-sidebar", branding.sidebar);
  aplicarFavicon(branding.logoUrl);

  document.title = `${branding.nombreSistema} — ${branding.nombreCorto}`;
}

/**
 * Apunta el `<link rel="icon">` al logo de la institución. Sin logo, deja el
 * favicon por defecto del index.html (falla cerrado, sin ícono roto).
 */
function aplicarFavicon(logoUrl: string | null): void {
  if (!logoUrl) return;
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  // El index.html declara type="image/svg+xml"; el logo del tenant puede ser
  // PNG/JPG, así que quitamos el type para no mentirle al navegador.
  link.removeAttribute("type");
  if (link.href !== logoUrl) link.href = logoUrl;
}
